import { COUNTER_CONFIRMATIONS } from './counters.js';

/**
 * PMS uç noktalarının döndürebildiği hata kodları (plan M7 Task 6a): ekranlar her birini iki
 * dilde anlatır [NR496 4.1.5]; `packages/ui/src/locales.test.ts` bunu denetler. Sonraki aşamalar
 * (M7b–M7e) listeyi genişletir.
 */
const PMS_BASE_ERROR_CODES = [
  // Ekipman ağacı ve kartı (Task 1)
  'EQUIPMENT_CYCLE',
  'VESSEL_MISMATCH',
  'GROUP_NOT_AT_ROOT',
  'DUPLICATE_EQUIPMENT_CODE',
  // Equipment Tag No. gemide tekil (ADR 0063 §8)
  'DUPLICATE_EQUIPMENT_TAG',
  'EQUIPMENT_HAS_CHILDREN',
  'DECOMMISSION_REASON_REQUIRED',
  'EQUIPMENT_PARENT_DELETED',
  'INVALID_EQUIPMENT',
  // Sayaçlar [NR496 4.5.1] (Task 2)
  'INVALID_KIND',
  'INVALID_HOURS',
  'INVALID_DATE',
  'FUTURE_DATE',
  'NEGATIVE_VALUE',
  'NOT_AFTER_PREVIOUS_READING',
  'PREVIOUS_METER_FINAL_REQUIRED',
  'METER_START_REQUIRED',
  'METER_START_MISMATCH',
  'METER_CHANGE_REASON_REQUIRED',
  'CONFIRMATION_REQUIRED',
  'CUMULATIVE_MISMATCH',
  'COUNTER_NOT_ENABLED',
  'EQUIPMENT_DELETED',
  'INVALID_READING_SOURCE',
  'SUPERSEDES_MISMATCH',
  // Yazma yolu ve senkron (Task 4)
  'INITIAL_PULL_PENDING',
  'EQUIPMENT_EXISTS',
  'SHIP_IS_WRITER',
  'OFFICE_IS_WRITER',
  'ROW_VERSION_CONFLICT',
  'INVALID_VESSEL_PARTICULARS',
  // Genel
  'NOT_FOUND',
  'FORBIDDEN',
  'VALIDATION_FAILED',
] as const;

/**
 * Durum izleme, CM içe aktarma ve arıza uçlarının hata kodları (M7c Task 15–20). Ekranlar M7c
 * Task 21a–21c'de bağlandı: liste `PMS_ERROR_CODES`'a katıldı, her kodun iki dilde metni var
 * [NR496 4.1.5]. Satır hataları ve uyarılar CM önizleme raporunda görünür.
 */
export const PMS_CONDITION_ERROR_CODES = [
  // Ölçüm noktası ve ölçüm (K18, K19, K21) [NR496 4.5.2]
  'LIMITS_REQUIRED',
  'INVALID_LIMITS',
  'PROVIDER_REQUIRED',
  'AUTO_DEFECT_OFF_REASON_REQUIRED',
  'BASELINE_DATE_REQUIRED',
  'UNIT_REQUIRED',
  'INVALID_POINT',
  'DUPLICATE_POINT',
  'POINT_DELETED',
  'POINT_IN_USE',
  'POINT_EQUIPMENT_FIXED',
  'POINT_MISMATCH',
  'UNIT_LOCKED',
  'UNIT_MISMATCH',
  'LIMIT_CHANGE_REASON_REQUIRED',
  'BASELINE_CHANGE_REASON_REQUIRED',
  'BASELINE_ORIGINAL_FIXED',
  'BASELINE_ORIGINAL_MISMATCH',
  'INVALID_VALUE',
  'LOCAL_DATE_MISMATCH',
  'LIMITS_MISMATCH',
  'RESULT_MISMATCH',
  'INVALID_MEASUREMENT',
  'IMPORT_BATCH_REQUIRED',
  'UNKNOWN_CONDITION_POINT',
  'PARAMETER_REQUIRED',
  'DELETE_REASON_REQUIRED',
  // CM içe aktarma (K20) [NR496 4.4.2d]
  'ALREADY_IMPORTED',
  'MISSING_COLUMN',
  'EMPTY_FILE',
  'TOO_MANY_ROWS',
  'IMPORT_HAS_ERRORS',
  'VALUES_ONLY_ON_BOARD',
  'INVALID_FILE',
  'FILE_TOO_LARGE',
  'UNSUPPORTED_FILE_TYPE',
  'UPLOAD_PURPOSE_MISMATCH',
  'INVALID_CM_BATCH',
  // Arıza ve breakdown (K24, K27) [NR496 4.3.2d, 4.5.4d]
  'INVALID_DEFECT',
  'DESCRIPTION_REQUIRED',
  'MEASUREMENT_REQUIRED',
  'INVALID_CONDITION',
  'NOT_IN_VERIFICATION',
  'ROOT_CAUSE_REQUIRED',
  'CORRECTIVE_ACTION_REQUIRED',
  'REPAIR_NOT_REPORTED',
  'REPAIR_REQUIRED',
  'CONDITION_AFTER_REQUIRED',
  'NOT_OPERATIONAL',
  'CLASS_NOTIFICATION_REQUIRED',
  'DEFECT_FIXED',
  'DEFECT_NO_MISMATCH',
  'DEFECT_CLOSED',
  'DEFECT_PENDING_CLOSURE',
  'MEASUREMENT_MISMATCH',
  'MEASUREMENT_NOT_FAILED',
  'REPAIR_WORK_ORDER_MISMATCH',
  'REPAIR_WORK_ORDER_EXISTS',
  'UNKNOWN_DEFECT',
  'DEFECT_MISMATCH',
  'DEFECT_REQUIRED',
  'RECORD_TYPE_MISMATCH',
  'ALREADY_SUBMITTED',
] as const;

/** CM önizleme raporundaki satır hatası ve uyarı kodları (`planCmImport`; K20). */
export const CM_ROW_CODES = [
  'UNKNOWN_EQUIPMENT',
  'POINT_REQUIRED',
  'UNIT_REQUIRED',
  'UNIT_MISMATCH',
  'INVALID_NUMBER',
  'INVALID_LIMITS',
  'LIMITS_REQUIRED',
  'LIMIT_SOURCE_REQUIRED',
  'INVALID_LIMIT_SOURCE',
  'PROVIDER_REQUIRED',
  'BASELINE_CHANGE_REASON_REQUIRED',
  'VALUES_ONLY_ON_BOARD',
  'MEASURED_AT_REQUIRED',
  'VALUE_REQUIRED',
  'INVALID_DATE',
  'FUTURE_DATE',
  'DUPLICATE_MEASUREMENT',
  'CONFLICTING_THRESHOLDS',
  'MEASUREMENT_EXISTS',
] as const;

/**
 * Ekranların anlattığı bütün PMS kodları (M7a temel liste, M7c durum izleme, CM satır kodları ve
 * PMS ayarları): her birinin iki dilde metni vardır [NR496 4.1.5] (`locales.test.ts`).
 */
/** Dolaşan bileşen (DNV-CP-0206 Sec.2 [4.3] c; ADR 0133 §6). */
const PMS_ROTABLE_ERROR_CODES = [
  'INVALID_ROTABLE',
  'INVALID_ROTABLE_MOVEMENT',
  'ROTABLE_SERIAL_TAKEN',
  'ROTABLE_DELETED',
  'ROTABLE_INSTALLED',
  'ROTABLE_ALREADY_INSTALLED',
  'ROTABLE_NOT_INSTALLED',
  'ROTABLE_POSITION_MISMATCH',
  'ROTABLE_DATE_ORDER',
  'ROTABLE_HOURS_DECREASE',
] as const;

export const PMS_ERROR_CODES = [
  ...PMS_BASE_ERROR_CODES,
  ...PMS_ROTABLE_ERROR_CODES,
  ...PMS_CONDITION_ERROR_CODES,
  ...CM_ROW_CODES,
] as const;
export type PmsErrorCode = (typeof PMS_ERROR_CODES)[number];

/** Sayaç onayları da ekranda iki dilde anlatılır (onay penceresi). */
export const PMS_CONFIRMATION_CODES = COUNTER_CONFIRMATIONS;
